import React, { useEffect } from 'react';
import { Drawer, Form, Input, InputNumber, Select, Slider, Button, Divider, Switch } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { AgentConfig } from '../types';

const { TextArea } = Input;
const { Option } = Select;

interface AgentConfigDrawerProps {
  open: boolean;
  onClose: () => void;
  config: AgentConfig;
  onSave: (config: AgentConfig) => void;
}

const AgentConfigDrawer: React.FC<AgentConfigDrawerProps> = ({
  open,
  onClose,
  config,
  onSave,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.setFieldsValue(config);
    }
  }, [open, config, form]);

  const handleFinish = (values: AgentConfig) => {
    onSave(values);
    onClose();
  };

  return (
    <Drawer
      title="Configuracion del Agente"
      placement="right"
      width={420}
      onClose={onClose}
      open={open}
      styles={{ body: { paddingBottom: 80 } }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={config}
      >
        <Form.Item
          label="Nombre del agente"
          name="name"
          rules={[{ required: true, message: 'Introduce un nombre' }]}
        >
          <Input placeholder="BuilderAgent" />
        </Form.Item>

        <Form.Item
          label="Modelo"
          name="model_name"
          rules={[{ required: true, message: 'Selecciona un modelo' }]}
        >
          <Select>
            <Option value="gemini-2.5-flash">gemini-2.5-flash</Option>
            <Option value="gemini-2.5-pro">gemini-2.5-pro</Option>
            <Option value="gemini-1.5-flash">gemini-1.5-flash</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Proveedor"
          name="model_provider"
          rules={[{ required: true }]}
        >
          <Select>
            <Option value="vertex_ai">Vertex AI</Option>
            <Option value="openai">OpenAI</Option>
            <Option value="anthropic">Anthropic</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Temperatura"
          name="temperature"
          tooltip="Controla la aleatoriedad: 0 es preciso, 1 es creativo"
        >
          <Slider min={0} max={1} step={0.1} marks={{ 0: '0', 0.5: '0.5', 1: '1' }} />
        </Form.Item>

        <Form.Item
          label="Limite de recursion"
          name="recursion_limit"
          rules={[{ required: true }]}
        >
          <InputNumber min={1} max={100} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Tarea"
          name="task"
          rules={[{ required: true, message: 'Describe la tarea' }]}
        >
          <TextArea rows={2} placeholder="Describe que debe hacer el agente..." />
        </Form.Item>

        <Form.Item
          label="Rol"
          name="role"
          rules={[{ required: true }]}
        >
          <Input placeholder="Expert Construction Assistant" />
        </Form.Item>

        <Form.Item
          label="Descripcion"
          name="description"
          rules={[{ required: true }]}
        >
          <TextArea rows={3} placeholder="Descripcion detallada del agente..." />
        </Form.Item>

        <Form.Item
          label="Output esperado"
          name="expected_output"
          rules={[{ required: true }]}
        >
          <TextArea rows={2} placeholder="Que tipo de salida debe producir?" />
        </Form.Item>

        <Divider />

        <Form.Item
          label="Update Knowledge Graph"
          name="update_kg"
          valuePropName="checked"
          tooltip="When enabled, a Knowledge Graph node is automatically appended to the flow to write ontology data to Neo4j"
        >
          <Switch />
        </Form.Item>

        {config.system_prompt && (
          <>
            <Divider />
            <Form.Item label="System Prompt (generado automaticamente)">
              <TextArea
                rows={4}
                value={config.system_prompt}
                readOnly
                style={{
                  background: '#f5f5f5',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: '#595959',
                }}
              />
            </Form.Item>
          </>
        )}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            block
            size="large"
          >
            Guardar Configuracion
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AgentConfigDrawer;
